<template>
    <form
        class="flex w-full flex-col items-stretch justify-between p-4 lg:p-8"
        @submit="onSubmit"
    >
        <div class="flex shrink flex-col items-stretch gap-3">
            <div>
                <button
                    class="flex items-center gap-4 text-start text-base font-medium text-black lg:mb-4 lg:text-xl"
                    type="button"
                    @click="emit('goBack')"
                >
                    <IconArrowRight class="size-4 rotate-180 lg:size-6" />
                    {{ props.address ? 'Изменить адрес' : 'Добавить адрес' }}
                </button>
            </div>
            <div>
                <InputAutocomplete
                    v-model:query="query"
                    name="address"
                    class="z-20"
                    label="Город, улица и дом"
                    @update:query="upd($event)"
                    :options="data || []"
                    :is-loading="isLoadingAddressSearch"
                    :display-value="(option: any) => option?.display_name"
                />
                <div class="text-red" v-if="house?.value.value == ''">
                    Введите номер дома и выберите вариант из списка
                </div>
            </div>
            <Transition name="fade">
                <div class="flex gap-3" v-if="!isDeliveryAvailable && address?.display_name?.length > 0">
                    <DangerTriangle></DangerTriangle>
                    <div>
                        Доставка на данный адрес недоступна. Введите другой адрес или
                        <button class="text-orange-200" @click="emit('goBackAndPickup')">выберите пункт самовывоза
                        </button>
                    </div>

                </div>
                </Transition>

                <div class="flex gap-3">
                    <InputText
                        class="flex-1"
                        name="entrance"
                        max-length="49"
                        label="Подьезд"
                    />
                    <InputText
                        class="flex-1"
                        name="door_phone"
                        max-length="49"
                        label="Код двери"
                    />
                    <div class="hidden">
                        <InputText
                            class="flex-1 "
                            name="house"
                            max-length="49"
                            label="Номер дома"
                        />
                    </div>

                </div>

            <div class="flex gap-3">
                <InputText
                    class="flex-1"
                    name="floor"
                    max-length="49"
                    label="Этаж"
                />
                <InputText
                    class="flex-1"
                    name="apartment"
                    max-length="49"
                    label="Номер квартиры"
                />
            </div>

            <InputTextarea
                name="comment"
                label="Комментарий"
                :rows="3"
                disable-resize
                class="mb-3"
            />
        </div>

        <div>
            <SimpleButton
                class="w-full px-4 py-4 text-sm font-medium uppercase"
                type="submit"
                :disabled="isPendingAddress || !isDeliveryAvailable"
                :class="!isDeliveryAvailable || isPendingAddress ? 'opacity-50' : ''"
            >
                <template v-if="props.address === undefined">
                    Добавить
                </template>
                <template v-else>
                    Сохранить
                </template>

            </SimpleButton>
        </div>
    </form>
</template>

<script setup lang="ts">
import * as yup from 'yup'
import type { LngLat } from '@yandex/ymaps3-types'
import { VueYandexMaps } from 'vue-yandex-maps'
import type { Address } from '~/interfaces/main'
import DangerTriangle from '~/components/icon/DangerTriangle.vue'

const props = defineProps<{
    address?: Address
    mapCoords: LngLat
}>()

const emit = defineEmits(['updateCoords', 'goBack', 'goBackAndPickup', 'createdNewAddress'])

const axiosPrivate = usePrivateAxiosInstance()

const upd = (e) => {
    from_geo = false
}

const { handleSubmit, setFieldValue } = useForm<any>({
    validationSchema: yup.object({
        address: yup
            .object({
                lat: yup.string(),
                lon: yup.string(),
                display_name: yup.string(),
                street: yup.string(),
                district: yup.string(),
                house: yup.string(),
                city: yup.string(),
            })
            .default(undefined)
            .required()
            .label('Город, улица и дом'),
        entrance: yup.string().label('Подъезд'),
        door_phone: yup.string().label('Код двери'),
        floor: yup.string().label('Этаж'),
        house: yup.string().required("Номер дома").label('Номер дома'),
        apartment: yup.string().label('Номер квартиры'),
        comment: yup.string().label('Комментарий'),
    }),
    initialValues: computed(() => ({
        address: {
            lat: props.address?.latitude,
            lon: props.address?.longitude,
            display_name: props.address?.full,
            city: props.address?.city,
            house: props.address?.house,
            street: props.address?.street,
            district: props.address?.district,
        },
        entrance: props.address?.entrance,
        door_phone: props.address?.door_phone,
        floor: props.address?.floor,
        apartment: props.address?.apartment,
        comment: props.address?.comment,
    })),
})

const address = useFieldValue<{ lat: string; lon: string; display_name: string; from_geo: boolean, city: string; house: string; street: string; district: string }>('address')
const house = useField<string>('house')
let from_geo = false
const coordinates = computed(() => {
    if (address.value) {
        return [Number(address.value.lon), Number(address.value.lat)]
    }
    return [37.617698, 55.755864]
})

if (props.address === undefined) {
    onMounted(() => {
        VueYandexMaps.ymaps()
            .geolocation.getPosition()
            .then(({ coords }) => {
                emit('updateCoords', [coords[0], coords[1]])
            })
    })
} else {
    emit('updateCoords', [Number(address.value.lon), Number(address.value.lat)])
}


watch(address, (add) => {
    setFieldValue("house", add.house)
}, {immediate: true})

watch([() => coordinates.value], () => {
    if(coordinates?.value && coordinates.value[0] == 37.617698) {
        return
    }
    emit('updateCoords', [coordinates.value, from_geo])
})
//watchEffect(() => emit('updateCoords', coordinates.value))

const isDeliveryAvailable = ref(true)
watch([() => props.mapCoords], () => {
    axiosPrivate
        .get('user/address/search/geo', {
            params: {
                latitude: props.mapCoords[1],
                longitude: props.mapCoords[0],
            },
        })
        .then((res) => {
            isDeliveryAvailable.value = res.data.is_delivery_available
            from_geo = true
            setFieldValue('address', res.data)
        })
})

const query = ref('')
const throttledQuery = refThrottled(query, 1000, undefined, false)

const { data, isLoading: isLoadingAddressSearch } = useAddressSearch(throttledQuery, (v) => v, true)


let useAddrFunc = useUpdateAddress

if (props.address === undefined) {
    useAddrFunc = useAddAddress
}

const { data: currentReceptionWay } = useCurrentReceptionWay()

const setCurrentReceptionWay = useSetCurrentReceptionWay()


const { mutateAsync, isPending: isPendingAddress } = useAddrFunc()

const onSubmit = handleSubmit((vals: any) => {
    let fullName = vals.address.display_name
    if (vals.door_phone) fullName += `, код двери ${vals.door_phone}`
    if (vals.apartment) fullName += `, кв. ${vals.apartment}`
    if (vals.entrance) fullName += `, подъезд ${vals.entrance}`

    const body = <any>{
        entrance: vals.entrance,
        door_phone: vals.door_phone,
        floor: vals.floor,
        apartment: vals.apartment,
        comment: vals.comment,
        latitude: vals.address.lat,
        longitude: vals.address.lon,
        address: vals.address.display_name,
        city: vals.address.city,
        house: vals.address.house,
        street: vals.address.street,
        district: vals.address.district,
        full_name: fullName,
    }

    if (props.address?.id) {
        body.id = props.address.id
    }
    mutateAsync(body).then((response) =>
        {
            if (!props.address?.id) {
                setCurrentReceptionWay({
                    type: 'delivery',
                    ...response.address,
                })
                emit('createdNewAddress')
            } else if (currentReceptionWay.value?.type === 'delivery' && currentReceptionWay.value.id == response.address.id) {
                setCurrentReceptionWay({
                    type: 'delivery',
                    ...response.address,
                })
            }
            emit('goBack')
        }
    )
})
</script>
